// Ported from obsidian-plugin/src/components/MessageList.tsx.
// Changes:
//   - <EmptyState> is inlined here (no separate file) and its /stats fetch
//     hits Django's generic proxy at /api/fastapi/stats instead of a
//     hard-coded base URL.
//   - VaultRef props removed — no Obsidian vault in the browser.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Message } from './Message';
import { RetrievalCard } from './RetrievalCard';
import { useEntityIndex } from './useEntityIndex';
import type { Stats } from './useLlmStatus';
import type {
  SourceDoc,
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  GraphTraversalEvent,
  ProfileEvent,
  ScoreThreshold,
  DetectedIOC,
  EntityLink,
} from '../../types';

export interface MessageData {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceDoc[];
  streaming?: boolean;
  progressStep?: PipelineStep | null;
  progressMessage?: string;
  progressCount?: number;
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  graphTraversalEvent?: GraphTraversalEvent | null;
  profileEvent?: ProfileEvent | null;
  scoreThreshold?: ScoreThreshold | null;
  planningTokens?: string;
  queryDetectedIocs?: DetectedIOC[];
  responseDetectedIocs?: DetectedIOC[];
  turnId?: string;          // server-issued; absent in temporary chats
}

interface EmptyStateProps {
  onSuggestionClick: (query: string) => void;
  stats: Stats | null;
  statsPending: boolean;
}

/** Example pool; a new chat shows three of them, shuffled. */
const SUGGESTIONS = [
  'What TTPs does Lazarus Group use?',
  'Recent reports mentioning Cobalt Strike',
  'Which actors exploited Citrix Bleed (CVE-2023-4966)?',
  "How did Emotet's delivery change over time?",
  'Which families share infrastructure with QakBot?',
  'Compare the tooling of APT28 and APT29',
  'Which ransomware families abuse vulnerable drivers?',
  'What is known about Volt Typhoon targets?',
  'Which loaders deliver IcedID?',
];
const SHOWN = 3;

/** Verbs the heading cycles through; the first one is where it rests. */
const VERBS = ['know', 'trace', 'uncover', 'attribute'];
const VERB_MS = 2600;

const COUNTS: { key: 'docs' | 'actors' | 'families'; label: string }[] = [
  { key: 'docs', label: 'reports' },
  { key: 'actors', label: 'actors' },
  { key: 'families', label: 'families' },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function pickSuggestions(exclude: string[] = []): string[] {
  const pool = SUGGESTIONS.filter((q) => !exclude.includes(q));
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, SHOWN);
}

/** Counts up from the previous value (0 on first paint) with an ease-out. */
function useCountUp(target: number | undefined, ms = 800): number | undefined {
  const [shown, setShown] = useState<number | undefined>(target);
  const fromRef = useRef(0);

  useEffect(() => {
    if (target == null) return;
    const from = fromRef.current;
    fromRef.current = target;
    if (from === target || prefersReducedMotion()) {
      setShown(target);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      setShown(Math.round(from + (target - from) * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);

  return shown;
}

function Count({ value, label, kind }: { value?: number; label: string; kind: string }) {
  const shown = useCountUp(value);
  return (
    <span className="malpedia-es-count" data-kind={kind}>
      <b>{shown != null ? shown.toLocaleString('en-US') : '—'}</b> {label}
    </span>
  );
}

/**
 * The heading's last word cycles through VERBS. All words share one grid
 * cell; the cell's width follows the active word so the centred line
 * glides instead of jumping. Pauses while hovered or the tab is hidden;
 * static under reduced motion.
 */
function RotatingVerb() {
  const [i, setI] = useState(0);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [paused, setPaused] = useState(false);
  const refs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    if (paused || prefersReducedMotion()) return;
    const id = window.setInterval(() => {
      if (!document.hidden) setI((n) => (n + 1) % VERBS.length);
    }, VERB_MS);
    return () => window.clearInterval(id);
  }, [paused]);

  useEffect(() => {
    const measure = () => setWidth(refs.current[i]?.offsetWidth);
    measure();
    // Webfonts can land after first paint and change the width.
    document.fonts?.ready.then(measure).catch(() => {});
  }, [i]);

  return (
    <span
      className="malpedia-es-verbs"
      style={width ? { width } : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {VERBS.map((v, n) => (
        <span
          key={v}
          ref={(el) => { refs.current[n] = el; }}
          className="malpedia-es-verb"
          data-state={n === i ? 'active' : n === (i + VERBS.length - 1) % VERBS.length ? 'prev' : 'next'}
          aria-hidden={n !== i}
        >
          {v}?
        </span>
      ))}
    </span>
  );
}

/** Small graph glyph: edges draw in, nodes pop, one node keeps a slow pulse. */
function GraphMark() {
  const nodes: [number, number, number][] = [
    [32, 12, 4.2], [12, 26, 3.2], [50, 30, 3.6], [22, 50, 3.4], [44, 52, 2.8],
  ];
  const edges: [number, number][] = [[0, 1], [0, 2], [1, 3], [2, 4], [3, 4], [1, 2]];
  return (
    <svg className="malpedia-es-mark" viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id="malpedia-es-mark-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--atlas-accent)' }} />
          <stop offset="1" style={{ stopColor: 'var(--atlas-accent-2)' }} />
        </linearGradient>
      </defs>
      <g className="malpedia-es-mark-edges">
        {edges.map(([a, b], k) => (
          <line
            key={k}
            x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]}
            pathLength={1}
            style={{ animationDelay: `${0.08 * k}s` }}
          />
        ))}
      </g>
      <g className="malpedia-es-mark-nodes">
        {nodes.map(([x, y, r], k) => (
          <circle key={k} cx={x} cy={y} r={r} style={{ animationDelay: `${0.35 + 0.07 * k}s` }} />
        ))}
      </g>
      <circle className="malpedia-es-mark-pulse" cx={nodes[0][0]} cy={nodes[0][1]} r={nodes[0][2]} />
    </svg>
  );
}

function EmptyState({ onSuggestionClick, stats, statsPending }: EmptyStateProps) {
  const llmDown = stats?.llm_status && stats.llm_status !== 'ok';
  const [shown, setShown] = useState(() => pickSuggestions());

  return (
    <div className="malpedia-empty-state">
      <GraphMark />

      <h2 className="malpedia-es-title" aria-label="What do you want to know?">
        What do you want to <RotatingVerb />
      </h2>

      <p className="malpedia-es-counts" aria-label="Corpus at a glance">
        {statsPending && !stats ? (
          <span className="malpedia-es-skel" role="status" aria-label="Loading" />
        ) : (
          COUNTS.map((c) => <Count key={c.key} kind={c.key} value={stats?.[c.key]} label={c.label} />)
        )}
      </p>

      {llmDown && (
        <p className="malpedia-es-warn" role="alert">
          {stats.llm_status === 'not_configured'
            ? 'No LLM backend configured — chat will not work.'
            : `LLM unavailable: ${stats.llm_status!.replace(/^unreachable:\s*/, '')}`}
        </p>
      )}

      <div className="malpedia-es-suggestions" role="group" aria-label="Example questions">
        {shown.map((q, n) => (
          <button
            key={q}
            type="button"
            className="malpedia-es-suggestion"
            style={{ animationDelay: `${0.3 + 0.07 * n}s` }}
            onClick={() => onSuggestionClick(q)}
          >
            <span>{q}</span>
            <span className="malpedia-es-arrow" aria-hidden="true">→</span>
          </button>
        ))}
      </div>

      <button type="button" className="malpedia-es-shuffle" onClick={() => setShown((cur) => pickSuggestions(cur))}>
        <span aria-hidden="true">↻</span> other examples
      </button>
    </div>
  );
}

interface MessageListProps {
  messages: MessageData[];
  onSuggestionClick?: (query: string) => void;
  stats?: Stats | null;
  statsPending?: boolean;
}

// Union the corpus-wide index with the retrieval-scoped entities surfaced
// for this turn. Retrieval entries win on conflict — they carry the
// scoring/strategy info the static index doesn't have.
function mergeEntityLinks(
  corpus: EntityLink[],
  graph: GraphTraversalEvent | null | undefined,
): EntityLink[] | undefined {
  const retrieval: EntityLink[] = graph
    ? graph.nodes
        .filter(
          (n): n is typeof n & { url: string } =>
            (n.label === 'MalwareFamily' || n.label === 'ThreatActor') && !!n.url,
        )
        .map((n) => ({ name: n.name, label: n.label, url: n.url }))
    : [];

  if (corpus.length === 0 && retrieval.length === 0) return undefined;

  const byKey = new Map<string, EntityLink>();
  for (const link of corpus) {
    byKey.set(link.name.toLowerCase(), link);
  }
  for (const link of retrieval) {
    // Preserve aliases from the corpus entry if retrieval supplied none.
    const key = link.name.toLowerCase();
    const existing = byKey.get(key);
    if (existing?.aliases && !link.aliases) {
      byKey.set(key, { ...link, aliases: existing.aliases });
    } else {
      byKey.set(key, link);
    }
  }
  return [...byKey.values()];
}

export function MessageList({ messages, onSuggestionClick, stats, statsPending = false }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const entityIndex = useEntityIndex();

  // Stable corpus-wide entity list — actors + families, each carrying its
  // aliases. Fed to every assistant <Message> so the linkifier can mark
  // names mentioned in prose even when retrieval didn't surface them.
  const corpusEntities: EntityLink[] = useMemo(() => {
    if (!entityIndex) return [];
    return [...entityIndex.actors, ...entityIndex.families];
  }, [entityIndex]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <EmptyState
        onSuggestionClick={onSuggestionClick || (() => {})}
        stats={stats ?? null}
        statsPending={statsPending}
      />
    );
  }

  const lastMsg = messages[messages.length - 1];
  const isThinking =
    lastMsg?.role === 'assistant' && lastMsg.streaming && !lastMsg.content;

  return (
    <div className="malpedia-message-list">
      {messages.map((msg) => (
        <React.Fragment key={msg.id}>
          {msg.role === 'assistant' && (
            <RetrievalCard
              streaming={msg.streaming}
              hasContent={!!msg.content}
              progressStep={msg.progressStep}
              progressMessage={msg.progressMessage}
              progressCount={msg.progressCount}
              completedSteps={msg.completedSteps}
              stepDetails={msg.stepDetails}
              planningEvent={msg.planningEvent}
              routingEvent={msg.routingEvent}
              graphTraversalEvent={msg.graphTraversalEvent}
              profileEvent={msg.profileEvent}
              scoreThreshold={msg.scoreThreshold}
              sources={msg.sources}
              planningTokens={msg.planningTokens}
              detectedIocs={msg.queryDetectedIocs}
              answerChars={msg.content?.length}
            />
          )}
          {!(msg.id === lastMsg?.id && isThinking) && (
            <Message
              role={msg.role}
              content={msg.content}
              sources={msg.sources}
              streaming={msg.streaming}
              completedSteps={msg.completedSteps}
              stepDetails={msg.stepDetails}
              planningEvent={msg.planningEvent}
              routingEvent={msg.routingEvent}
              profileEvent={msg.profileEvent}
              scoreThreshold={msg.scoreThreshold}
              detectedIocs={
                msg.role === 'user' ? msg.queryDetectedIocs : msg.responseDetectedIocs
              }
              entityLinks={
                msg.role === 'assistant'
                  ? mergeEntityLinks(corpusEntities, msg.graphTraversalEvent)
                  : undefined
              }
              turnId={msg.role === 'assistant' ? msg.turnId : undefined}
            />
          )}
        </React.Fragment>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
